/**
 * Temporary-credit constants, application wire types, and ops-event
 * outbox (router#2000, epic #1997 E3).
 *
 * Long-term and temporary share one grant object (#1998). This module is
 * the temporary-only contract: admission numbers, the application shape,
 * and the events the lifecycle scan emits. Delivery (Feishu) is E6;
 * the customer-detail block is E4; the monitor page is E5. This file
 * does not talk to `account_balance_alerts`.
 */

import { CREDIT_APPLICATION_STATUSES, type CreditApplicationStatus } from './internal-credit.ts';

/** Absolute cap in yuan. Compared directly to the operator CNY input (router#2036). */
export const TEMPORARY_CREDIT_CAP_CNY = 35_000;

/** Inclusive CNY-cent form of {@link TEMPORARY_CREDIT_CAP_CNY}. Equal-to-cap is admitted. */
export const TEMPORARY_CREDIT_CAP_CNY_CENTS = TEMPORARY_CREDIT_CAP_CNY * 100;

/** Grant `ends_at` = expected arrival date + this many calendar days (UTC+8). */
export const TEMPORARY_CREDIT_BUFFER_DAYS = 5;

/** Rolling window for the application-frequency gate. */
export const TEMPORARY_CREDIT_WINDOW_DAYS = 90;

/** Inclusive max applications inside the window; the next is refused. */
export const TEMPORARY_CREDIT_MAX_APPLICATIONS_IN_WINDOW = 2;

/** One-shot bill due date = issue date + this many calendar days (UTC+8). */
export const TEMPORARY_CREDIT_ONE_SHOT_DUE_DAYS = 7;

/**
 * Arrival-shortfall trigger: actual inbound / expected < this ratio.
 * Stored as a reduced fraction so the comparison is integer.
 */
export const TEMPORARY_CREDIT_ARRIVAL_SHORTFALL_NUMERATOR = 70;
export const TEMPORARY_CREDIT_ARRIVAL_SHORTFALL_DENOMINATOR = 100;

/** Lead time for the expiring-soon event, calendar days before `ends_at`. */
export const TEMPORARY_CREDIT_EXPIRY_NOTICE_DAYS = 2;

export const TEMPORARY_CREDIT_EVIDENCE_MAX_CHARS = 4_000;

export const TEMPORARY_CREDIT_APPLICATION_ID_PREFIX = 'tcapp_';

/** `approval_requests.target_type` for a temporary-credit application. */
export const TEMPORARY_CREDIT_APPLICATION_TARGET_TYPE = 'temporary_credit_application';

export const TEMPORARY_CREDIT_EVIDENCE_ENTITY_TYPE = 'temporary_credit_evidence';

export const CREDIT_OPS_EVENT_TYPES = [
  'temporary_credit_expiring',
  'temporary_credit_arrival_shortfall',
  'temporary_credit_one_shot_issued',
  'credit_due_soon_7d',
  'credit_due_soon_3d',
  'credit_due_today',
  'credit_overdue_frozen',
  'credit_stop_notice',
  'credit_service_stopped',
  'credit_collections',
  'credit_overdue_cleared',
  'credit_exemption_opened',
  'credit_exemption_revoked',
  'credit_overdue_reminder',
] as const;
export type CreditOpsEventType = (typeof CREDIT_OPS_EVENT_TYPES)[number];

export const CREDIT_OPS_EVENT_AUDIENCES = ['customer', 'internal'] as const;
export type CreditOpsEventAudience = (typeof CREDIT_OPS_EVENT_AUDIENCES)[number];

export function isCreditOpsEventType(value: string): value is CreditOpsEventType {
  for (const candidate of CREDIT_OPS_EVENT_TYPES) {
    if (candidate === value) return true;
  }
  return false;
}

export function isCreditOpsEventAudience(value: string): value is CreditOpsEventAudience {
  for (const candidate of CREDIT_OPS_EVENT_AUDIENCES) {
    if (candidate === value) return true;
  }
  return false;
}

export interface TemporaryCreditApplication {
  id: string;
  account_id: string;
  requested_limit_cny_cents: number | null;
  requested_limit_usd_micros: number;
  payment_evidence_text: string | null;
  payment_evidence_attachment_key: string | null;
  expected_arrival_amount_cny_cents: number | null;
  expected_arrival_amount_usd_micros: number;
  /** Locked USD_CNY rate at approval; null while pending. */
  applied_rate: number | null;
  /** UTC+8 calendar date 'YYYY-MM-DD'. */
  expected_arrival_date: string;
  status: CreditApplicationStatus;
  requester_ops_user_id: string;
  grant_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface SubmitTemporaryCreditApplicationRequest {
  org_id: string;
  requested_limit_cny_cents: number;
  expected_arrival_amount_cny_cents: number;
  expected_arrival_date: string;
  payment_evidence_text?: string | null;
  payment_evidence_attachment_key?: string | null;
  requester_ops_user_id: string;
}

export interface SubmitTemporaryCreditApplicationResponse {
  outcome: 'pending_approval';
  application: TemporaryCreditApplication;
}

export interface GetTemporaryCreditApplicationsResponse {
  account_id: string;
  org_id: string;
  applications: TemporaryCreditApplication[];
}

export interface CreditOpsEvent {
  id: string;
  event_type: CreditOpsEventType;
  audience: CreditOpsEventAudience;
  account_id: string;
  grant_id: string | null;
  statement_id: string | null;
  payload_json: string;
  created_at: string;
}

export interface RunTemporaryCreditLifecycleRequest {
  cursor?: string | null;
  limit?: number;
  /** Test-only clock override (epoch ms); production omits it. */
  now_ms?: number;
}

export interface RunTemporaryCreditLifecycleResponse {
  ok: boolean;
  scanned: number;
  repaid: number;
  expired: number;
  billed: number;
  notices: number;
  next_cursor: string | null;
}

export const EXPIRE_TEMPORARY_CREDIT_HANDLER = 'expire_temporary_credit';

const CALENDAR_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export function isCalendarDateYmd(value: unknown): value is string {
  if (typeof value !== 'string' || !CALENDAR_DATE_PATTERN.test(value)) {
    return false;
  }
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  const utc = Date.UTC(year, month - 1, day);
  const dt = new Date(utc);
  return (
    dt.getUTCFullYear() === year &&
    dt.getUTCMonth() === month - 1 &&
    dt.getUTCDate() === day
  );
}

/**
 * True when the requested CNY cents are strictly above the yuan cap.
 * Equal-to-cap is admitted. The product number is yuan; the wire is
 * integer CNY cents (router#2036). No FX round-trip.
 */
export function temporaryCreditExceedsCnyCap(
  requestedLimitCnyCents: number,
  capCny: number = TEMPORARY_CREDIT_CAP_CNY,
): boolean {
  if (!Number.isSafeInteger(requestedLimitCnyCents) || requestedLimitCnyCents <= 0) {
    return true;
  }
  if (!Number.isSafeInteger(capCny) || capCny <= 0) {
    return true;
  }
  return requestedLimitCnyCents > capCny * 100;
}

/**
 * True when actual inbound is positive and strictly below 70% of the
 * expected arrival. Zero inbound is not a shortfall — that is expiry
 * without repayment.
 */
export function temporaryCreditArrivalIsShortfall(
  actualInboundUsdMicros: number,
  expectedArrivalUsdMicros: number,
): boolean {
  if (!Number.isSafeInteger(actualInboundUsdMicros) || actualInboundUsdMicros <= 0) {
    return false;
  }
  if (!Number.isSafeInteger(expectedArrivalUsdMicros) || expectedArrivalUsdMicros <= 0) {
    return false;
  }
  return (
    actualInboundUsdMicros * TEMPORARY_CREDIT_ARRIVAL_SHORTFALL_DENOMINATOR <
    expectedArrivalUsdMicros * TEMPORARY_CREDIT_ARRIVAL_SHORTFALL_NUMERATOR
  );
}

export function isTemporaryCreditApplicationStatus(
  value: unknown,
): value is CreditApplicationStatus {
  return (
    typeof value === 'string' &&
    (CREDIT_APPLICATION_STATUSES as readonly string[]).includes(value)
  );
}
